import { useCallback, useContext, useEffect, useState } from 'react';
import { AuthContext } from 'context/AuthContext';
import { useForm, SubmitHandler } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';

import { AzureProfile } from 'sso/azure/ui/AzureProfile';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from 'sso/azure/authConfig';
import { getAzureToken } from 'sso/azure/azure-api';
import Button from 'components/ui/button';
import { BiDesktop, BiMobileAlt } from 'react-icons/bi';
import Logo from 'components/logo';
import { removeStorageItem, setStorageItem } from 'utils/storage';

type Inputs = {
  email: string;
  pass: string;
};

export function Login() {
  const navigate = useNavigate();
  const { instance } = useMsal();
  const [loading, setLoading] = useState(false);
  const [invalidUser, setInvalidUser] = useState(false);
  const { loginWithToken, login } = useContext(AuthContext);
  const [useSSO, setUseSSO] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<Inputs>();
  const onSubmit: SubmitHandler<Inputs> = async data => {
    if (data.email && data.pass) {
      await doLogin(data.email, data.pass);
    }
  };

  function loginSSO() {
    setLoading(true);
    setUseSSO(true);
    setInvalidUser(false);
  }

  async function handleLoginSSO(response: any) {
    // console.log('handleLoginSSO', response);

    try {
      const password = await getAzureToken();
      const username = response.userPrincipalName;

      if (username && password) {
        await login({ username, password });
        navigate('/');
      }
    } catch (e) {
      setLoading(false);
      setInvalidUser(true);
      setUseSSO(false);
    }
  }

  const doLogin = useCallback(
    async (username: string, password: string) => {
      try {
        setLoading(true);
        setErrorMessage('');
        await login({ username, password });
        navigate('/');
      } catch (e: any) {
        setLoading(false);
        setInvalidUser(true);

        if (typeof e.message === 'string' && e.message === 'password incorrect for player') {
          setErrorMessage('O nome de usuário ou senha digitados estão incorretos. Por favor, tente novamente.');
        } else {
          setErrorMessage('Ocorreu um erro com sua solicitação. Por favor, tente novamente.');
        }
      }
    },
    [login, navigate],
  );

  useEffect(() => {
    async function doLoginWithToken(token: string) {
      try {
        await loginWithToken(token);
        navigate('/');
      } catch (e) {
        setLoading(false);
        setInvalidUser(true);
      }
    }

    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const agent = params.get('agent');
    const secret = params.get('secret');
    const tokenItem = params.get('tokenItem');

    if (token) {
      setLoading(true);
      doLoginWithToken(token);
      removeStorageItem('readOnly');
    } else if (agent && secret) {
      setLoading(true);
      doLogin(agent, secret);
      removeStorageItem('readOnly');
    } else if (tokenItem) {
      setLoading(true);
      const decodedToken = atob(tokenItem);
      const tokenParts = decodedToken.split(':');
      const id = tokenParts[0];
      const password = tokenParts[1];

      setStorageItem('readOnly', 'true');
      doLogin(id, password);
    } else {
      // setUseSSO(true);
      setInvalidUser(true);
    }
  }, [loginWithToken, login, navigate, doLogin]);

  return (
    <div className="absolute left-0 right-0 min-h-full m-auto px-5 md:w-1/2 max-w-[600px] flex flex-col items-center justify-center py-10">
      <div className="w-48 lg:w-72  relative">
        <Logo />
      </div>

      <p className="text-center text-bold text-xl mt-10">A participação na FLAP 360° é voluntária.</p>

      <p className="text-center text-lg">
        Para participar leia o{' '}
        <Link to="/regulamento" className="text-yellow">
          regulamento
        </Link>{' '}
        e manifeste sua ciência e concordância com os termos e condições da ação.
      </p>

      {/* {loading && !useSSO && !invalidUser && (
        <div className="w-full border text-blue-dark bg-yellow p-3 mt-5 rounded-xl text-center text-gray font-bold text-xl">
          CARREGANDO...
        </div>
      )} */}

      {invalidUser && (
        <>
          <div className="mt-10 w-full rounded-lg sm:max-w-md p-0 border border-white">
            <div className="p-6 space-y-3">
              <h2 className="block text-center text-xl text-white font-bold leading-tight tracking-tight md:text-2xl dark:text-white">
                Entre com seu login e senha
              </h2>
              <form className="space-y-4 md:space-y-6" onSubmit={handleSubmit(onSubmit)}>
                <div>
                  <label htmlFor="email" className="text-yellow block mb-2 text-sm font-medium dark:text-white">
                    E-mail:
                  </label>
                  <input
                    type="email"
                    id="email"
                    className="bg-gray-50 border border-gray-300 text-gray-900 rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    placeholder="name@company.com"
                    {...register('email', { required: true })}
                    disabled={loading}
                  />
                </div>
                <div>
                  <label htmlFor="password" className="text-yellow block mb-2 text-sm font-medium dark:text-white">
                    Senha:
                  </label>
                  <input
                    type="password"
                    id="password"
                    placeholder="••••••••"
                    className="bg-gray-50 border border-gray-300 text-gray-900 rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    {...register('pass', { required: true })}
                    disabled={loading}
                  />
                </div>
                <div className="flex items-center justify-center">
                  <button
                    className="hover:ring hover:ring-white rounded-md shadow-xl font-bold px-4 w-full text-xl p-2 bg-yellow text-blue-dark w-full"
                    type="submit"
                    disabled={loading}
                  >
                    <div className="flex flex-row items-center justify-center space-x-2 p-0 leading-none">
                      {loading ? (
                        <span>Carregando...</span>
                      ) : (
                        <>
                          <BiDesktop />
                          <span>Clique para entrar</span>
                        </>
                      )}
                    </div>
                  </button>
                </div>
                {!!errorMessage && !!errorMessage.length && <p className="block text-center text-pink">{errorMessage}</p>}
              </form>
            </div>
          </div>
        </>
      )}

      {/* {useSSO ? <AzureProfile onAuthenticate={handleLoginSSO} /> : null} */}
    </div>
  );
}
